import { createFetch } from '@vueuse/core'
import { destr } from 'destr'
import type { ComputedRef } from 'vue'

import { getCsrfToken, logout, performRefresh } from '@/composables/useAuthSession'
import { getDeviceFingerprint } from '@/utils/helpers/deviceFingerprint'

const baseFetch = createFetch({
  baseUrl: import.meta.env.VITE_API_BASE_URL || '/api',
  fetchOptions: {
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include',
  },
  options: {
    refetch: true,
    async beforeFetch({ options }) {
      const fingerprint = await getDeviceFingerprint()
      const csrfToken = getCsrfToken()

      const headers: Record<string, string> = {
        ...((options.headers as Record<string, string>) || {}),
        'X-Device-Fingerprint': fingerprint,
      }

      if (csrfToken)
        headers['X-CSRF-Token'] = csrfToken

      options.headers = headers

      return { options }
    },
    afterFetch(ctx) {
      const { data, response } = ctx

      let parsedData = null
      try {
        parsedData = destr(data)
      }
      catch (error) {
        console.error(error)
      }

      return { data: parsedData, response }
    },
    async onFetchError(ctx) {
      const { data, response } = ctx

      let parsedData
      try {
        parsedData = destr(data)

        if (
          response?.status !== undefined
          && response.status >= 400
          && typeof parsedData === 'object'
          && parsedData !== null
          && 'message' in parsedData
          && typeof parsedData.message === 'object'
          && parsedData.message !== null
          && 'message' in parsedData.message
        ) {
          // #55985: preserva o `code` aninhado (ex.: 423 AI_PROCESSING_LOCKED)
          // antes de achatar `message` para string, senao o 423 nao e detectado.
          const nested = parsedData.message as Record<string, unknown>
          const root = parsedData as Record<string, unknown>

          if (nested.code !== undefined && root.code === undefined)
            root.code = nested.code

          parsedData.message = nested.message
        }
      }
      catch (error) {
        console.error(error)
      }

      return { error: parsedData, response }
    },
  },
})

export const useApi = <T = unknown>(
  url: string | ComputedRef<string>,
  fetchOptions?: RequestInit,
) => {
  const instance = fetchOptions
    ? (baseFetch as (...a: unknown[]) => unknown)(url, fetchOptions) as ReturnType<typeof baseFetch>
    : (baseFetch as (...a: unknown[]) => unknown)(url) as ReturnType<typeof baseFetch>

  const originalThen = instance.then!.bind(instance)

  ;(instance as { then: unknown }).then = (onFulfilled?: ((value: unknown) => unknown) | null, onRejected?: ((reason: unknown) => unknown) | null) => {
    return originalThen(async result => {
      const status = result.response.value?.status

      const urlValue = typeof url === 'string' ? url : url.value

      if (status === 401 && !urlValue.includes('/oauth2/')) {
        const { ok: refreshed, serverError } = await performRefresh()

        if (refreshed) {
          await result.execute(true)

          if (result.response.value?.status === 401) {
            logout()

            return onFulfilled ? onFulfilled(result) : result
          }
        }
        else if (!serverError) {
          logout()

          return onFulfilled ? onFulfilled(result) : result
        }
        else {
          return onFulfilled ? onFulfilled(result) : result
        }
      }

      if (result.response.value?.status === 403) {
        const parsedData = result.error.value || result.data.value
        const redirectUrl = (parsedData as Record<string, unknown>)?.url as string | undefined
        if (redirectUrl)
          window.location.href = redirectUrl
      }

      return onFulfilled ? onFulfilled(result) : result
    }, onRejected)
  }

  return instance as ReturnType<typeof baseFetch<T>> & PromiseLike<ReturnType<typeof baseFetch<T>>>
}
